(() => {
  if (window.__ckNewsletterFormInit) return;
  window.__ckNewsletterFormInit = true;

  /**
   * Handles newsletter form submission
   * Uses Shopify contact form with Mailchimp sync option
   */
  const FORM_SELECTOR = '#FooterNewsletter';
  const SUCCESS_SELECTOR = '[data-newsletter-success]';
  const ERROR_SELECTOR = '[data-newsletter-error]';

  const showMessage = (form, message, type = 'success') => {
    const successEl = form.querySelector(SUCCESS_SELECTOR);
    const errorEl = form.querySelector(ERROR_SELECTOR);

    // Hide both first
    if (successEl) {
      successEl.classList.add('hidden');
      successEl.textContent = '';
    }
    if (errorEl) {
      errorEl.classList.add('hidden');
      errorEl.textContent = '';
    }

    // Show the appropriate one
    if (type === 'success' && successEl) {
      successEl.textContent = message;
      successEl.classList.remove('hidden');
    } else if (type === 'error' && errorEl) {
      errorEl.textContent = message;
      errorEl.classList.remove('hidden');
    }
  };

  const setLoading = (form, loading) => {
    const button = form.querySelector('ck-button[type="submit"]');
    if (!button) return;

    if (loading) {
      // Store original width before loading
      if (!button.dataset.originalWidth) {
        const buttonElement = button.shadowRoot?.querySelector('button');
        if (buttonElement) {
          const computedStyle = window.getComputedStyle(buttonElement);
          const width = computedStyle.width;
          button.dataset.originalWidth = width;
          // Apply as min-width to prevent shrinking
          buttonElement.style.minWidth = width;
        }
      }
      button.loading = true;
    } else {
      button.loading = false;
      // Restore original width after loading
      const buttonElement = button.shadowRoot?.querySelector('button');
      if (buttonElement && button.dataset.originalWidth) {
        buttonElement.style.minWidth = button.dataset.originalWidth;
      }
    }
  };

  /**
   * Submit to Shopify contact form with retry logic for rate limits
   * @param {FormData} formData - Form data to submit
   * @param {number} retryCount - Number of retries remaining
   * @param {Function} onRetry - Callback when retrying (to update UI)
   * @returns {Promise<{success: boolean, message: string}>}
   */
  const submitToShopify = async (formData, retryCount = 2, onRetry = null) => {
    try {
      const response = await fetch('/contact', {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          Accept: 'application/json, text/html, */*',
        },
      });

      const html = await response.text();

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Handle rate limiting with retry (keep loader showing)
      if (response.status === 429) {
        if (retryCount > 0) {
          // Log technical error to console
          console.warn(
            `[Newsletter] Rate limit (429) - Retrying... (${retryCount} attempts remaining)`
          );

          // Notify UI that we're retrying (loader stays visible)
          if (onRetry) {
            onRetry(retryCount);
          }

          // Wait before retry (exponential backoff: 2s, 4s)
          const waitTime = (3 - retryCount) * 2000;
          await new Promise((resolve) => setTimeout(resolve, waitTime));

          // Retry the request
          return submitToShopify(formData, retryCount - 1, onRetry);
        }

        // All retries exhausted - show friendly message
        console.error('[Newsletter] Rate limit (429) - All retries exhausted');
        return {
          success: false,
          message: 'Something went wrong. Please try again in a few minutes.',
        };
      }

      // Check for "already subscribed" errors (common Mailchimp/Shopify sync issue)
      const bodyText = doc.body.textContent?.toLowerCase() || '';
      const isAlreadySubscribed =
        bodyText.includes('already subscribed') ||
        bodyText.includes('already exists') ||
        bodyText.includes('duplicate') ||
        bodyText.includes('is already a customer') ||
        bodyText.includes('email has already been taken');

      // Check for errors
      const errorElement =
        doc.querySelector('.errors, [role="alert"]') ||
        doc.querySelector('.form-error, .error-message') ||
        doc.querySelector('ul.errors li');

      const hasErrors = errorElement || doc.querySelector('form.errors') || isAlreadySubscribed;

      // Check for success
      const hasSuccess =
        doc.querySelector('[data-newsletter-success]')?.textContent?.trim() ||
        doc.querySelector('.success-message')?.textContent?.trim() ||
        (response.ok && !hasErrors && response.status !== 429) ||
        response.status === 302 ||
        response.redirected;

      if (hasSuccess) {
        return { success: true, message: `Thanks — you're subscribed.` };
      }

      // Handle "already subscribed" - show friendly message
      if (isAlreadySubscribed) {
        return { success: true, message: "You're already subscribed!" };
      }

      if (hasErrors) {
        let errorText = errorElement?.textContent?.trim();
        if (!errorText && errorElement) {
          errorText =
            Array.from(errorElement.querySelectorAll('li'))
              .map((li) => li.textContent?.trim())
              .filter(Boolean)
              .join(', ') || errorElement.textContent?.trim();
        }

        // Log technical error but show friendly message
        console.warn('[Newsletter] ❌ Error detected from Shopify:', {
          errorText: errorText?.substring(0, 200),
          errorElement: errorElement ? errorElement.outerHTML.substring(0, 200) : null,
          bodyText: bodyText.substring(0, 300),
        });

        // Map to friendly message
        return {
          success: false,
          message: 'Something went wrong. Please try again in a few minutes.',
        };
      }

      // If we get here and response is 200 OK, it might actually be success
      // (Shopify sometimes returns success without clear indicators)
      if (response.ok && response.status === 200) {
        return { success: true, message: `Thanks — you're subscribed.` };
      }

      // Server errors - log but show friendly message
      if (response.status >= 500) {
        console.error(`[Newsletter] Server error (${response.status})`);
        return {
          success: false,
          message: 'Something went wrong. Please try again in a few minutes.',
        };
      }

      // Unknown errors - log but show friendly message
      console.warn(`[Newsletter] Unknown error (${response.status})`, {
        html: html.substring(0, 500),
      });
      return {
        success: false,
        message: 'Something went wrong. Please try again in a few minutes.',
      };
    } catch (error) {
      // Network errors - log but show friendly message
      console.error('[Newsletter] Network error:', error);
      return {
        success: false,
        message: 'Something went wrong. Please check your connection and try again.',
      };
    }
  };

  const init = () => {
    const form = document.querySelector(FORM_SELECTOR);
    if (!form) return;

    let isSubmitting = false;

    form.addEventListener(
      'submit',
      async (event) => {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        // Prevent duplicate submissions
        if (isSubmitting) {
          return;
        }

        // Get form data
        const formData = new FormData(form);

        // Validate
        if (!form.checkValidity()) {
          form.reportValidity();
          return;
        }

        isSubmitting = true;
        setLoading(form, true);
        // Hide any existing messages
        showMessage(form, '', 'success');

        try {
          const email = formData.get('contact[email]');
          const emailValue = email ? email.trim().toLowerCase() : '';

          if (!emailValue || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue)) {
            showMessage(form, 'Please enter a valid email address.', 'error');
            setLoading(form, false);
            isSubmitting = false;
            return;
          }

          // Prepare form data for Shopify
          formData.delete('h-captcha-response');
          formData.delete('g-recaptcha-response');

          // Callback to notify UI during retries (loader stays visible)
          const onRetry = (retryCount) => {
            // Loader is already showing, we just keep it visible
            console.log(`[Newsletter] 🔄 Retrying... (${retryCount} attempts remaining)`);
          };

          // Submit to Shopify contact form (with retry logic for rate limits)
          // Loader will stay visible during retries
          const result = await submitToShopify(formData, 2, onRetry);

          if (result.success) {
            showMessage(form, result.message, 'success');
            form.reset();
          } else {
            showMessage(form, result.message, 'error');
          }
        } catch (error) {
          console.error('[Newsletter] Unexpected error:', error);
          showMessage(
            form,
            'Something went wrong. Please check your connection and try again.',
            'error'
          );
        } finally {
          setLoading(form, false);
          isSubmitting = false;
        }

        // Ensure form doesn't submit normally
        return false;
      },
      { capture: true }
    );
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
